/*
  Edit.js - Cost Centres Edit Component
*/

// NPM
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

// I18N
import formattedMessages from '../../../messages/account/EditCostCenterFormattedMessages';

// NETWORK
import CostCenterContacts from './CostCenterContacts';
import useAdministratorRequest from '../../../requests/AdministratorRequest';
import useCostCentreRequest from '../../../requests/CostCentreRequest';

const CostCentreEdit = ({ centre, errorHandler, handleBackClick }) => {
  const administratorRequest = useAdministratorRequest(errorHandler);
  const costCentreRequest = useCostCentreRequest(errorHandler);

  const [state, setState] = React.useState({
    contacts: [],
    edits: {},
    isSaving: false,
    error: null,
  });

  // Memoize the sort function as it doesn't depend on any external values
  const sortContacts = useMemo(() => {
    return (contacts = []) => {
      if (!Array.isArray(contacts)) return [];

      return [...contacts].sort((a, b) => {
        const nameA = (a?.contact_name || '').toLowerCase();
        const nameB = (b?.contact_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    };
  }, []);

  // Use ref to ensure administratorRequest doesn't cause unnecessary rerenders
  const administratorRequestRef = useRef(administratorRequest);
  useEffect(() => {
    administratorRequestRef.current = administratorRequest;
  }, [administratorRequest]);

  // Load contacts only once when component mounts
  useEffect(() => {
    const loadContacts = async () => {
      try {
        const response =
          await administratorRequestRef.current.getAdministrators(
            '', // letter (empty string for all)
            1, // pageNumber
            1000 // pageLength (large number to get all contacts)
          );

        // If response is in the paginated format, use items array
        const contacts = Array.isArray(response)
          ? response
          : response?.items || [];

        setState((prev) => ({
          ...prev,
          contacts: sortContacts(contacts),
          error: null,
        }));
      } catch (error) {
        console.error('Error loading contacts:', error);
        setState((prev) => ({
          ...prev,
          contacts: [],
          error: 'Error loading contacts',
        }));
      }
    };

    loadContacts();
  }, []); // Empty dependency array since we're using refs

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setState((prev) => ({
      ...prev,
      edits: {
        ...prev.edits,
        [name]:
          value === 'None' && name === 'customer_contact_id' ? null : value,
      },
    }));
  }, []);

  const validateForm = useCallback(() => {
    const { edits } = state;
    if (!edits.cost_centre_name && !centre.cost_centre_name) {
      return {
        isValid: false,
        error: 'Requires a Name.',
      };
    }
    return {
      isValid: true,
      error: '',
    };
  }, [centre.cost_centre_name, state]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { isValid, error } = validateForm();

      if (!isValid) {
        setState((prev) => ({
          ...prev,
          error,
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        isSaving: true,
        error: null,
      }));

      try {
        await costCentreRequest.editCostCentre(
          centre.cost_centre_id,
          state.edits,
          errorHandler
        );
        handleBackClick(true);
      } catch (error) {
        console.error('Error updating cost centre:', error);
        setState((prev) => ({
          ...prev,
          error: 'Error updating cost centre',
          isSaving: false,
        }));
      }
    },
    [
      centre.cost_centre_id,
      costCentreRequest,
      handleBackClick,
      state.edits,
      validateForm,
    ]
  );

  return (
    <>
      {/* Form */}
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3} sx={{ maxWidth: 600 }}>
          {/* Cost Centre Name */}
          <Stack direction="row" spacing={2} alignItems="center">
            <InputLabel required sx={{ width: 150, textAlign: 'left' }}>
              {formattedMessages.name}
            </InputLabel>
            <TextField
              required
              name="cost_centre_name"
              defaultValue={centre.cost_centre_name}
              onChange={handleFormChange}
              size="small"
              fullWidth
            />
          </Stack>

          {/* Contact Select */}
          <Stack direction="row" spacing={2} alignItems="center">
            <InputLabel sx={{ width: 150, textAlign: 'left' }}>
              {formattedMessages.contactName}
            </InputLabel>
            <FormControl fullWidth size="small">
              <Select
                name="customer_contact_id"
                value={
                  state.edits.customer_contact_id ||
                  centre.customer_contact_id ||
                  ''
                }
                onChange={handleFormChange}
              >
                <MenuItem value="">None</MenuItem>
                {(state.contacts || []).map((contact) => (
                  <MenuItem
                    key={contact.customer_contact_id}
                    value={contact.customer_contact_id}
                  >
                    {contact.contact_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Error Display */}
          {state.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {state.error}
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} sx={{ mt: 4 }} alignSelf="end">
            <Button
              variant="link"
              color="secondary"
              onClick={() => handleBackClick(false)}
              disabled={state.isSaving}
              sx={{
                textTransform: 'none',
                textDecoration: 'underline',
              }}
            >
              {formattedMessages.cancel}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              type="submit"
              disabled={state.isSaving}
            >
              {state.isSaving ? 'Saving...' : formattedMessages.save}
            </Button>
          </Stack>
        </Stack>
      </Box>
      <CostCenterContacts centre={centre} />
    </>
  );
};

export default CostCentreEdit;
